import {
  DataLakeQuerySortDirection,
  ExecuteSimpleSearchQueryInput,
  ExecuteUberSearchInput,
  Parameter,
  SimpleSearchSource,
} from 'Generated/schema';
import cloneDeep from 'lodash/cloneDeep';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { v4 as uuidv4 } from 'uuid';
import { RelativeTimeUnits } from 'pounceV2';
import { PresetOptions, TimeframeValue } from 'Components/TimeframeSelection/constants';
import { ColumnProperty, Filters, Metadata, UnifiedFormValues } from '../../types';
import { CrossLogSearchFormValues, TimeFrameEnum } from '../types';
import { createFiltersString } from '../../helpers/simpleQueryFormHelpers';
import { parseQueryFilter, transformQueryFilters } from '../../helpers/helpers';
import { createInitialFiltersState } from '../../useQueryBuilderStateHandler';
import {
  getTimeStringsFromValues,
  getISO8601DurationFromCustomRelativeTime,
} from '../CrossLogSearchForm/contexts/hooks/useUpdatePantherFlowQueryTextFromFormValues';
import { getIso8601DurationUnitAndValue } from '../CrossLogSearchForm/contexts/hooks/useUpdateFormValuesFromPantherFlowQueryText/helpers';

// Extend dayjs with duration plugin for duration calculations
dayjs.extend(duration);

export const isCrossLogSearchValues = (
  values: UnifiedFormValues,
): values is CrossLogSearchFormValues => 'databases' in values;

export const transformQBToCrossLogSearchFormValues = (
  values: UnifiedFormValues,
): CrossLogSearchFormValues => {
  let _values = values;
  // We need to convert the old filters format into the new format
  if (Array.isArray(values.filters)) {
    _values = cloneDeep(values);
    _values.filters = {
      ors: [
        {
          uuid: uuidv4(),
          ands: values.filters,
        },
      ],
    };
  }
  if (!_values.filters) {
    _values.filters = createInitialFiltersState();
  }
  if (isCrossLogSearchValues(_values)) {
    return _values;
  }
  const { database, table, ...rest } = _values;
  return {
    ...rest,
    databases: database ? [database] : [],
    tables: table ? [table] : [],
    timeFrame: TimeFrameEnum.Last20Minutes,
    relativeNumberVal: 5,
    relativeTimeUnit: RelativeTimeUnits.Mins,
  };
};

export const transformUnifiedFormValuesToSimpleSearchValues = (values: UnifiedFormValues) => {
  if ('database' in values) {
    return values;
  }
  const { databases, tables, ...rest } = values;
  return {
    ...rest,
    database: databases?.length ? databases[0] : null,
    table: tables?.length ? tables[0] : null,
  };
};

export const transformTableToLogType = (tableName: string, schemas: string[]) => {
  if (!schemas.length) {
    return tableName;
  }
  const normalizedTableName = tableName.replace(/_/g, '.');
  const logType = schemas.find(schema => normalizedTableName === schema.toLowerCase());
  return logType ?? tableName;
};

export const transformRelativeTimeUnit = (
  relativeTimeUnit: RelativeTimeUnits,
): dayjs.ManipulateType => {
  switch (relativeTimeUnit) {
    case RelativeTimeUnits.Mins:
      return 'minutes';
    case RelativeTimeUnits.Hours:
      return 'hours';
    case RelativeTimeUnits.Days:
      return 'days';
    case RelativeTimeUnits.Weeks:
      return 'weeks';
    case RelativeTimeUnits.Months:
      return 'months';
    case RelativeTimeUnits.Years:
      return 'years';
    default:
      return 'minutes';
  }
};

export type GetStartAndEndTimesParams = {
  values: TimeframeValue;
  // unix timestamp
  nowOverride?: number;
};

export type GetStartAndEndTimesReturn = {
  startTime: dayjs.Dayjs | null;
  endTime: dayjs.Dayjs | null;
};

export const getStartAndEndTimes = ({
  values,
  nowOverride,
}: GetStartAndEndTimesParams): GetStartAndEndTimesReturn => {
  const { timeFrame, startTime, endTime, relativeNumberVal, relativeTimeUnit } = values;
  let returnStartTime = startTime === null ? null : dayjs(startTime);
  let returnEndTime = endTime === null ? null : dayjs(endTime);
  // If the time frame is TimeFrameEnum.AllTime, return null for start and end time since those are not used
  // in that case
  if (timeFrame && timeFrame === TimeFrameEnum.AllTime) {
    return {
      startTime: null,
      endTime: null,
    };
  }
  if (timeFrame && timeFrame === TimeFrameEnum.RelativeTimeCustom) {
    const now = nowOverride ? dayjs(nowOverride).utc().toDate() : dayjs().utc().toDate();
    const [_startTime, _endTime] = [
      dayjs(now)
        .subtract(relativeNumberVal ?? 5, transformRelativeTimeUnit(relativeTimeUnit))
        .startOf('minute')
        .toDate(),
      dayjs(now).endOf('minute').toDate(),
    ];
    return {
      startTime: dayjs(_startTime),
      endTime: dayjs(_endTime),
    };
  }
  if (timeFrame && timeFrame !== TimeFrameEnum.Custom && timeFrame !== TimeFrameEnum.AllTime) {
    const { getValue } = PresetOptions.find(({ id }) => id === timeFrame);
    const now = nowOverride ? dayjs(nowOverride).utc().toDate() : dayjs().utc().toDate();
    const [_startTime, _endTime] = getValue(now);
    returnStartTime = dayjs(_startTime);
    returnEndTime = dayjs(_endTime);
  }

  return {
    startTime: returnStartTime,
    endTime: returnEndTime,
  };
};

// A custom relative time (e.g. "33 minutes") is encoded as an ISO 8601 duration string (PT33M) so it
// persists and restores the same way preset relative times already do. Absolute ("Custom") ranges and
// an unset timeframe have no relative representation.
const getRelativeTimeForPayload = (values: CrossLogSearchFormValues): string | undefined => {
  const { timeFrame, relativeNumberVal, relativeTimeUnit } = values;
  if (timeFrame === TimeFrameEnum.RelativeTimeCustom) {
    if (!relativeNumberVal || !relativeTimeUnit) {
      return undefined;
    }
    return getISO8601DurationFromCustomRelativeTime(relativeNumberVal, relativeTimeUnit);
  }
  if (timeFrame === TimeFrameEnum.Custom || !timeFrame) {
    return undefined;
  }
  return timeFrame;
};

type generateCrossLogSearchPayloadParams = {
  values: CrossLogSearchFormValues;
  previousQueryId?: string;
  shouldFilterOutIncompleteFilterRows?: boolean;
  // unix timestamp
  nowOverride?: number;
  source?: SimpleSearchSource;
  parameters?: Parameter[];
};

export const generateCrossLogSearchPayload = ({
  values,
  previousQueryId,
  shouldFilterOutIncompleteFilterRows = true,
  nowOverride,
  source,
  parameters,
}: generateCrossLogSearchPayloadParams): ExecuteSimpleSearchQueryInput => {
  const {
    databases,
    tables,
    limit,
    sortDirection,
    filters,
    pantherFlowInput,
    usePantherFlow,
    timeFrame,
  } = values;
  const { startTime, endTime } = getStartAndEndTimes({
    values,
    nowOverride,
  });

  const relativeTime = getRelativeTimeForPayload(values);

  const payload: ExecuteUberSearchInput = {
    params: {
      sortDir: sortDirection,
      databases,
      tables,
      relativeTime: timeFrame === null ? null : relativeTime,
      limit: Number(limit),
      language: usePantherFlow ? 'pantherflow' : 'simplesearch',
      ...(parameters !== undefined ? { parameters } : {}),
    },
  };

  // Add the start and end time if they exist. They will be set to null if the time frame is TimeFrameEnum.AllTime
  if (startTime) {
    payload.params.startTime = dayjs.utc(startTime).toISOString();
  }
  if (endTime) {
    payload.params.endTime = dayjs.utc(endTime).toISOString();
  }

  if (previousQueryId) {
    payload.copyUserConfigQueryId = previousQueryId;
  }

  // Add the source if it exists
  if (source) {
    payload.params.source = source;
  }

  payload.params.filters = '';
  if (usePantherFlow) {
    if (pantherFlowInput !== undefined && pantherFlowInput.length > 0) {
      payload.params.filters = pantherFlowInput;
    }
  } else if (filters.ors.length > 0) {
    payload.params.filters = createFiltersString(filters, shouldFilterOutIncompleteFilterRows);
  }

  return payload;
};

// When a search is run from the UI and the time values are set to null, the startTime and endTime
// are set to '0001-01-01T00:00:00Z' on the back end. This is a special value that indicates that
// the time range is all time.
export const DEFAULT_ALL_TIME_START_AND_END_TIME = '0001-01-01T00:00:00Z';

// Restores the timeframe controls from a persisted relativeTime. A preset duration maps back to its
// preset option; any other ISO 8601 duration (e.g. PT33M) is a custom relative time, decoded into its
// number + unit. An absent relativeTime means an absolute range.
const getTimeFrameFromRelativeTime = (
  relativeTime?: string,
): {
  timeFrame: TimeFrameEnum;
  relativeNumberVal?: number;
  relativeTimeUnit?: RelativeTimeUnits;
} => {
  if (!relativeTime) {
    return { timeFrame: TimeFrameEnum.Custom };
  }
  if (PresetOptions.some(({ id }) => id === relativeTime)) {
    return { timeFrame: relativeTime as unknown as TimeFrameEnum };
  }
  const [relativeNumberVal, relativeTimeUnit] = getIso8601DurationUnitAndValue(relativeTime);
  return { timeFrame: TimeFrameEnum.RelativeTimeCustom, relativeNumberVal, relativeTimeUnit };
};

export const transformMetadataToCrossLogSearchFormValues = (
  metadata: Metadata,
  properties: ColumnProperty[],
): CrossLogSearchFormValues => {
  const parsedFilters = parseQueryFilter(metadata.simpleSearchQueryFilter);
  const filters = (
    parsedFilters ? transformQueryFilters(parsedFilters, properties) : createInitialFiltersState()
  ) as Filters;

  const { timeFrame, relativeNumberVal, relativeTimeUnit } = getTimeFrameFromRelativeTime(
    metadata.relativeTime,
  );

  const retObj = {
    usePantherFlow: Boolean(metadata.query),
    startTime: dayjs(metadata.startTime),
    endTime: dayjs(metadata.endTime),
    databases: metadata.databases,
    tables: metadata.tables,
    limit: metadata.limit,
    filters,
    relativeNumberVal: relativeNumberVal ?? metadata.relativeNumberVal ?? 5,
    relativeTimeUnit: relativeTimeUnit ?? metadata.relativeTimeUnit ?? RelativeTimeUnits.Mins,
    sortDirection: metadata.sortDirection ?? DataLakeQuerySortDirection.Desc,
    timeFrame,
    pantherFlowInput: metadata.query ?? '',
  };

  // In this special case, we want to manually set the timeFrame, startTime and endTime to null since that's what
  // was originally sent in from the UI. It will ensure the form state is rendered correctly.
  const isAllTimeWithoutTimeRange =
    !metadata.relativeTime &&
    metadata.startTime === DEFAULT_ALL_TIME_START_AND_END_TIME &&
    metadata.endTime === DEFAULT_ALL_TIME_START_AND_END_TIME;
  if (isAllTimeWithoutTimeRange) {
    retObj.timeFrame = null;
    retObj.startTime = null;
    retObj.endTime = null;
  }

  return retObj;
};

export const sortItemsBySelectedFirst = (
  items: any[],
  selectedItems: string[],
  sortKey?: string,
) => {
  // If items is null or undefined, return an empty array
  if (!items) {
    return [];
  }

  // If selectedItems is null or undefined, return the original items
  if (!selectedItems) {
    return items;
  }

  // Return a new array with the items sorted by the selected items to
  // ensure React can detect the change
  return [...items].sort((a, b) => {
    // if a and b are in the selected items, use existing order
    if (
      selectedItems.includes(sortKey ? a?.[sortKey] : a) &&
      selectedItems.includes(sortKey ? b?.[sortKey] : b)
    ) {
      return items.indexOf(a) - items.indexOf(b);
    }
    // if a is in the selected items, it should be first
    if (selectedItems.includes(sortKey ? a?.[sortKey] : a)) {
      return -1;
    }
    // if b is in the selected items, it should be first
    if (selectedItems.includes(sortKey ? b?.[sortKey] : b)) {
      return 1;
    }
    // otherwise, use existing order
    return 0;
  });
};

const DAYS_PER_MONTH = 30;

export const isTimeRangeEqual = (
  formValues: CrossLogSearchFormValues,
  queryInfoStartTime: string,
  queryInfoEndTime: string,
) => {
  const { startTimeString, endTimeString } = getTimeStringsFromValues(formValues);
  const isCustom = formValues.timeFrame === TimeFrameEnum.Custom;
  const isAllTime = formValues.timeFrame === TimeFrameEnum.AllTime;

  // The startTimeString and endTimeString are null if the user manually sets a time that's unsyncable. In that case,
  // we want to return false so that the user can see the error message.
  if (startTimeString === null && endTimeString === null) {
    return false;
  }

  // Absolute time range
  if (isCustom) {
    return queryInfoStartTime === startTimeString && queryInfoEndTime === endTimeString;
  }

  // All time
  if (isAllTime) {
    return queryInfoStartTime === '' && queryInfoEndTime === '';
  }

  // Presets and Relative custom time range
  let startTimeDurationFromValuesInMinutes = dayjs.duration(startTimeString).asMinutes();

  // If the startTimeString is in months, we need to convert it to days first
  if (!startTimeString.startsWith('PT') && startTimeString.endsWith('M')) {
    const startTimeDurationFromValuesInMonths = Math.floor(
      dayjs.duration(startTimeString).asMonths(),
    );
    // Convert the startTimeString to days using 30 days per month. We have to do this manually
    // because the dayjs duration method uses 30.41667 days per month which messes up the comparison.
    const startTimeDurationFromValuesInDays = startTimeDurationFromValuesInMonths * DAYS_PER_MONTH;
    startTimeDurationFromValuesInMinutes = dayjs
      .duration(startTimeDurationFromValuesInDays, 'days')
      .asMinutes();
  }

  // The wasm parser doesn't return units greater than days, so we need to convert the
  // both the queryInfoStartTime and startTimeString to minutes to compare with each other.
  return dayjs.duration(queryInfoStartTime).asMinutes() === startTimeDurationFromValuesInMinutes;
};

/**
 * Columns
 */

export const getColumnNamesFromDependentQueryData = dependentQueryData => {
  const knownColumns: Set<string> = new Set();
  const dataList = dependentQueryData ?? [];
  dataList.forEach(obj => {
    const rowObject = obj?.row_object ?? {};
    Object.keys(rowObject).forEach(key => knownColumns.add(key));
  });
  return [...knownColumns.keys()].sort((a, b) => {
    // We want to sort so that columns that contain '()' are at the end
    if (a?.includes('()') && !b?.includes('()')) {
      return 1;
    }

    if (!a?.includes('()') && b?.includes('()')) {
      return -1;
    }

    return 0;
  });
};

const EMPTY_HIDDEN_STATIC_COLUMNS: ReadonlySet<string> = new Set();

// These names match the default column headers used in the CSV download / async export column
// selection. Keep them aligned with results-table static columns (`UserConfigContext.STATIC_COLUMN_IDS`)
// and QueryBuilderResultsTableContainerV3. `hiddenStaticColumns` mirrors the grid's per-column hide
// state so the CSV exports exactly the static columns the table is showing (EPD-5465).
const getLeadingStaticColumns = (
  dataLakeType: string | undefined,
  hiddenStaticColumns: ReadonlySet<string>,
  includeTableName: boolean,
): string[] => {
  // Use 'databaseName' for Databricks, 'databasename' for others. The hidden-column id is always
  // 'databasename' (the grid column id) regardless of the data-lake-specific accessor key.
  const databaseColumn = dataLakeType === 'databricks' ? 'databaseName' : 'databasename';
  const columns: string[] = [];
  if (!hiddenStaticColumns.has('p_timeline')) {
    columns.push('p_timeline');
  }
  if (!hiddenStaticColumns.has('databasename')) {
    columns.push(databaseColumn);
  }
  // `tablename` represents the results-table "Log Type" (p_log_type) column, so it is included
  // only when that column is present in the table (EPD-5465).
  if (includeTableName) {
    columns.push('tablename');
  }
  return columns;
};

const getTrailingStaticColumns = (hiddenStaticColumns: ReadonlySet<string>): string[] =>
  hiddenStaticColumns.has('row_object') ? [] : ['row_object'];

export const getColumnNamesFromUserConfigData = (
  userConfig,
  dataLakeType?: string,
  hiddenStaticColumns: ReadonlySet<string> = EMPTY_HIDDEN_STATIC_COLUMNS,
): string[] => {
  if (!userConfig?.tableProperties) {
    return [];
  }

  const selectedColumnPaths: string[] = userConfig.tableProperties.map(
    ({ propertyPath }) => propertyPath,
  );

  // No expressed column preference — no fields selected and no static column hidden — means the user
  // hasn't customized the table, so return [] and let the export fall back to the backend default of
  // all columns. This also covers callers rendered without a live UserConfigContext (CSV
  // notifications, dashboard widgets), which always see the default empty config and must export the
  // full result set rather than a trimmed list (EPD-5465).
  if (!selectedColumnPaths.length && hiddenStaticColumns.size === 0) {
    return [];
  }

  // p_log_type is surfaced via the 'tablename' static column; track its presence to decide whether
  // to emit `tablename`, and drop the raw path so it isn't duplicated.
  const hasLogTypeColumn = selectedColumnPaths.includes('p_log_type');
  const userSelectedColumns = selectedColumnPaths.filter(columnName => columnName !== 'p_log_type');

  // Emit the explicit list of columns the results table is showing (visible static columns +
  // user-selected fields) so the CSV mirrors the table. Hiding a default static column takes effect
  // here even when no custom fields are selected (EPD-5465).
  return [
    ...getLeadingStaticColumns(dataLakeType, hiddenStaticColumns, hasLogTypeColumn),
    ...userSelectedColumns,
    ...getTrailingStaticColumns(hiddenStaticColumns),
  ].map(col => col.replaceAll('[', '\\[').replaceAll(']', '\\]'));
};

// Column paths passed to CSV/async download mutations; should stay consistent with
// getColumnNamesFromUserConfigData and hidden-static handling (EPD-5465).
export const getSelectedColumnPaths = (
  userConfig,
  dependentQueryData,
  language = 'simpleSearch',
  dataLakeType?: string,
  hiddenStaticColumns: ReadonlySet<string> = EMPTY_HIDDEN_STATIC_COLUMNS,
) => {
  // Return empty array if dependentQueryData is empty
  if (!dependentQueryData || dependentQueryData.length === 0) {
    return [];
  }

  const isTimeBasedData =
    language === 'simpleSearch' || Boolean(dependentQueryData?.[0]?.p_timeline);
  // if the query is time based, that means it is not using a `summarize` operator,
  // and we want to use all the columns from the userConfig, which includes the static columns.
  // Otherwise, we want to use only the columns the user has chosen to display which is found in
  // the results of the query.
  // Escape all brackets in the column name before wrapping it in brackets
  // because the BE treats brackets as a special character in jsonutil
  return isTimeBasedData
    ? getColumnNamesFromUserConfigData(userConfig, dataLakeType, hiddenStaticColumns).map(col =>
        col.replaceAll('[', '\\[').replaceAll(']', '\\]'),
      )
    : getColumnNamesFromDependentQueryData(dependentQueryData).map(col => {
        // custom named top level columns might include dots in the name which don't indicate a
        // nested json field, it is just their name (e.g. "agg.count()")
        const c = col.replaceAll('[', '\\[').replaceAll(']', '\\]');
        return col.includes('.') ? `['${c}']` : c;
      });
};
